import type { InferContractRouterOutputs } from '@orpc/contract'
import type { BaseContract } from 'pixai-contract/api/orpc'
import { useCallback, useEffect, useId, useMemo, useState } from 'react'
import {
  Button as AriaButton,
  Tag as AriaTag,
  TagGroup as AriaTagGroup,
  TagList as AriaTagList,
} from 'react-aria-components'
import { useTranslation } from 'react-i18next'
import { twJoin } from 'tailwind-merge'
import { tv } from 'tailwind-variants'
import FluentChevronLeft20Regular from '~icons/fluent/chevron-left-20-regular'
import IcBaselineDiscord from '~icons/ic/baseline-discord'
import MaterialSymbolsCheckRounded from '~icons/material-symbols/check-rounded'
import MaterialSymbolsEditRounded from '~icons/material-symbols/edit-rounded'
import MaterialSymbolsLinkRounded from '~icons/material-symbols/link-rounded'
import MdiShareVariant from '~icons/mdi/share-variant'
import { OutboundLink } from 'src/components/OutboundLink'
import { UIButton } from 'src/components/ui/Button'
import {
  UIDialogContent,
  UIDialogFooter,
  UIDialogHeader,
  UIDialogResponsive,
  UIDrawer,
} from 'src/components/ui/Modal'
import { UIInput, UITextArea } from 'src/components/ui/TextField'
import { cx } from 'src/utils/cx'
import { useIsMobile } from 'src/utils/hooks/useBreakpoint'
import { getMediaUrl } from 'src/utils/media'
import { track } from 'src/utils/tracker'

type StyleKeyItem =
  InferContractRouterOutputs<BaseContract>['generationModel']['getStyleKeys'][number]

type StyleSelectDialogProps = {
  open: boolean
  onClose: () => void
  styleKeys: StyleKeyItem[]
  modelType: string
  selectedStyleKeyId?: string
  onApply: (styleKeyId: string) => void
  onClear?: () => void
  onCustomApply?: (custom: string) => void
  onRefIdApply?: (refId: string) => void
  onOpenSaveDialog?: (prefillText: string) => void
  customValue?: string
  refIdValue?: string
  initialCustomize?: boolean
}

const tagClassName = twJoin(
  'min-w-12 px-2 py-1 text-center rounded-md ring-1 ring-inset cursor-default text-xs',
  'ring-black/15',
  'group-selected:text-white group-selected:bg-black',
  'group-hover:bg-neutral-100 dark:group-hover:bg-white/10',
  'dark:ring-white/15',
  'dark:group-selected:text-black dark:group-selected:bg-white',
  'group-disabled:opacity-60',
)

const styleCardStyles = tv({
  base: 'relative flex flex-col rounded-xl overflow-hidden cursor-pointer transition-all m-1 ring-2 ring-offset-1 shadow-md',
  variants: {
    selected: {
      true: 'ring-purple-400 dark:ring-purple-600',
      false: 'ring-transparent hover:ring-zinc-400 dark:hover:ring-zinc-500',
    },
  },
})

const mobileActionButton = tv({
  base: 'flex-1 flex items-center justify-center gap-1 rounded-md py-1 text-xs transition-colors',
  variants: {
    variant: {
      secondary:
        'bg-black/5 dark:bg-white/10 hover:bg-black/10 dark:hover:bg-white/20',
      primary: 'bg-purple-500 text-white hover:bg-purple-600',
    },
  },
})

export function StyleSelectDialog(props: StyleSelectDialogProps) {
  const {
    open,
    onClose,
    styleKeys,
    modelType,
    selectedStyleKeyId,
    onApply,
    onClear,
    onCustomApply,
    onRefIdApply,
    onOpenSaveDialog,
    customValue: externalCustomValue,
    refIdValue: externalRefIdValue,
    initialCustomize,
  } = props
  const { t } = useTranslation(['generation'])
  const isMobile = useIsMobile()

  const [selectedId, setSelectedId] = useState<string | undefined>(
    selectedStyleKeyId,
  )
  const [selectedTags, setSelectedTags] = useState<Set<string>>(new Set())
  const [showcaseStyleId, setShowcaseStyleId] = useState<string | undefined>()
  const [showCustomize, setShowCustomize] = useState(false)
  const [customDraft, setCustomDraft] = useState('')
  const [refIdDraft, setRefIdDraft] = useState('')

  // Sync local selection when dialog opens with a different value
  useEffect(() => {
    if (open) {
      setSelectedId(selectedStyleKeyId)
      setShowcaseStyleId(undefined)
      setShowCustomize(!!initialCustomize)
      setCustomDraft(externalCustomValue ?? '')
      setRefIdDraft(externalRefIdValue ?? '')
    }
  }, [
    open,
    selectedStyleKeyId,
    initialCustomize,
    externalCustomValue,
    externalRefIdValue,
  ])

  // Collect all unique tags from style keys
  const allTags = useMemo(() => {
    const tagSet = new Set<string>()
    for (const item of styleKeys) {
      for (const tag of item.tags) {
        tagSet.add(tag)
      }
    }
    return Array.from(tagSet).map(tag => ({
      id: tag,
      label: t([`generation:style.tags.${tag}`, tag]),
    }))
  }, [styleKeys, t])

  // Filter styles by selected tags (intersection)
  const filteredStyles = useMemo(() => {
    if (selectedTags.size === 0) return styleKeys
    return styleKeys.filter(item =>
      [...selectedTags].every(tag => item.tags.includes(tag)),
    )
  }, [styleKeys, selectedTags])

  const selectedStyle = useMemo(
    () => styleKeys.find(s => s.id === selectedId),
    [styleKeys, selectedId],
  )

  const showcaseStyle = useMemo(
    () => styleKeys.find(s => s.id === showcaseStyleId),
    [styleKeys, showcaseStyleId],
  )

  const handleApply = useCallback(() => {
    if (selectedId) {
      onApply(selectedId)
      onClose()
    }
  }, [selectedId, onApply, onClose])

  const handleClearStyle = useCallback(() => {
    onClear?.()
    onClose()
  }, [onClear, onClose])

  const handleUse = useCallback(
    (styleKeyId: string) => {
      onApply(styleKeyId)
      setShowcaseStyleId(undefined)
      onClose()
    },
    [onApply, onClose],
  )

  const handleCustomConfirm = useCallback(() => {
    if (customDraft && onCustomApply) {
      onCustomApply(customDraft)
      onClose()
    }
  }, [customDraft, onCustomApply, onClose])

  const handleRefIdConfirm = useCallback(() => {
    if (refIdDraft && onRefIdApply) {
      onRefIdApply(refIdDraft)
      onClose()
    }
  }, [refIdDraft, onRefIdApply, onClose])

  const hasCustomize = !!(onCustomApply || onRefIdApply)

  return (
    <>
      <UIDialogResponsive
        open={open}
        onClose={onClose}
        dismissable
        className={isMobile ? '' : 'w-screen max-w-[800px]'}
      >
        <UIDialogHeader heading={t('generation:style.selector.title')}>
          {({ heading }) => (
            <div className="flex items-center justify-between w-full">
              {heading}
              <div className="flex items-center gap-1.5">
                <OutboundLink
                  href={t('generation:style.dc-channel-url')}
                  className={cx(
                    'flex items-center gap-1 text-xs px-2.5 py-1 rounded-md transition-colors',
                    'text-neutral-500 hover:bg-black/5 dark:hover:bg-white/5',
                  )}
                  onClick={() =>
                    track('style_dc_channel_click', {
                      source: 'style_selector',
                    })
                  }
                >
                  <IcBaselineDiscord className="size-3.5" />
                  {t('generation:style.dc-channel')}
                </OutboundLink>
                {hasCustomize && (
                  <AriaButton
                    aria-pressed={showCustomize}
                    onPress={() => setShowCustomize(v => !v)}
                    className={cx(
                      'flex items-center gap-1 text-xs px-2.5 py-1 rounded-md transition-colors',
                      showCustomize
                        ? 'bg-purple-500/10 text-purple-600 dark:text-purple-400'
                        : 'text-neutral-500 hover:bg-black/5 dark:hover:bg-white/5',
                    )}
                  >
                    <MaterialSymbolsEditRounded className="size-3.5" />
                    {t('generation:style.customize')}
                  </AriaButton>
                )}
              </div>
            </div>
          )}
        </UIDialogHeader>
        <UIDialogContent>
          {showCustomize ? (
            <CustomizePanel
              customDraft={customDraft}
              onCustomDraftChange={setCustomDraft}
              onCustomConfirm={onCustomApply ? handleCustomConfirm : undefined}
              refIdDraft={refIdDraft}
              onRefIdDraftChange={setRefIdDraft}
              onRefIdConfirm={onRefIdApply ? handleRefIdConfirm : undefined}
              onOpenSaveDialog={onOpenSaveDialog}
            />
          ) : isMobile ? (
            <MobileStyleLibrary
              allTags={allTags}
              selectedTags={selectedTags}
              onTagsChange={setSelectedTags}
              filteredStyles={filteredStyles}
              modelType={modelType}
              onShowcase={setShowcaseStyleId}
              onUse={handleUse}
              onClear={onClear ? handleClearStyle : undefined}
            />
          ) : (
            <DesktopStyleContent
              allTags={allTags}
              selectedTags={selectedTags}
              onTagsChange={setSelectedTags}
              filteredStyles={filteredStyles}
              modelType={modelType}
              selectedId={selectedId}
              onSelect={setSelectedId}
              selectedStyle={selectedStyle}
              onClear={onClear ? handleClearStyle : undefined}
            />
          )}
        </UIDialogContent>
        {!isMobile && !showCustomize && (
          <UIDialogFooter>
            <UIButton
              variant="filled"
              color="theme"
              disabled={!selectedId}
              onPress={handleApply}
            >
              {t('generation:style.selector.apply')}
            </UIButton>
          </UIDialogFooter>
        )}
      </UIDialogResponsive>

      {/* Mobile: Layer 2 — Showcase drawer */}
      {isMobile && showcaseStyle && (
        <UIDrawer
          open={!!showcaseStyleId}
          onClose={() => setShowcaseStyleId(undefined)}
          dismissable
        >
          <UIDialogHeader
            heading={t([
              `generation:model-style.${modelType}.${showcaseStyle.id}`,
              showcaseStyle.id,
            ])}
          >
            {({ heading }) => (
              <div className="flex items-center gap-2">
                <UIButton
                  variant="ghost"
                  icon={<FluentChevronLeft20Regular className="size-5" />}
                  className="-ml-1"
                  onPress={() => setShowcaseStyleId(undefined)}
                />
                {heading}
              </div>
            )}
          </UIDialogHeader>
          <UIDialogContent>
            <div className="columns-2 gap-2 p-2">
              {showcaseStyle.artworks
                .filter(a => a.mediaId)
                .map(artwork => (
                  <MediaThumbnail
                    key={artwork.mediaId}
                    artworkId={artwork.artworkId}
                    mediaId={artwork.mediaId}
                    masonry
                  />
                ))}
            </div>
          </UIDialogContent>
          <UIDialogFooter>
            <UIButton
              variant="filled"
              color="theme"
              className="w-full"
              onPress={() => handleUse(showcaseStyle.id)}
            >
              {t('generation:style.selector.apply')}
            </UIButton>
          </UIDialogFooter>
        </UIDrawer>
      )}
    </>
  )
}

/* ─── Customize panel ─── */

const confirmButtonClassName = twJoin(
  'flex-none p-2 rounded-md bg-purple-500 text-white',
  'hover:bg-purple-600 transition-colors',
  'disabled:opacity-30 disabled:pointer-events-none',
)

function CustomizePanel(props: {
  customDraft: string
  onCustomDraftChange: (value: string) => void
  onCustomConfirm?: () => void
  refIdDraft: string
  onRefIdDraftChange: (value: string) => void
  onRefIdConfirm?: () => void
  onOpenSaveDialog?: (prefillText: string) => void
}) {
  const {
    customDraft,
    onCustomDraftChange,
    onCustomConfirm,
    refIdDraft,
    onRefIdDraftChange,
    onRefIdConfirm,
    onOpenSaveDialog,
  } = props
  const { t } = useTranslation(['generation'])
  const customId = useId()
  const refIdId = useId()
  return (
    <div className="flex flex-col gap-6 py-2">
      {/* Custom style section */}
      {onCustomConfirm && (
        <div className="flex flex-col gap-2">
          <label
            htmlFor={customId}
            className="flex items-center gap-1.5 text-sm font-medium text-neutral-700 dark:text-neutral-300"
          >
            <MaterialSymbolsEditRounded className="size-4" />
            {t('generation:style.customize')}
          </label>
          <p className="text-xs text-neutral-500 dark:text-neutral-400">
            {t('generation:style.customize-description')}
          </p>
          <div className="flex gap-2">
            <UITextArea
              id={customId}
              variant="rounded-normal"
              placeholder={t('generation:style.custom-placeholder')}
              value={customDraft}
              onChange={e => onCustomDraftChange(e.target.value)}
              rows={3}
              className="flex-1 text-base sm:text-sm resize-none"
            />
            <AriaButton
              onPress={onCustomConfirm}
              isDisabled={!customDraft}
              className={cx(confirmButtonClassName, 'self-end')}
            >
              <MaterialSymbolsCheckRounded className="size-5" />
            </AriaButton>
          </div>
          {onOpenSaveDialog && (
            <AriaButton
              onPress={() => onOpenSaveDialog(customDraft)}
              className="flex items-center gap-1.5 text-xs text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-200 transition-colors self-start"
            >
              <MdiShareVariant className="size-3.5" />
              {t('generation:style.save-as-ref')}
            </AriaButton>
          )}
        </div>
      )}

      {/* Ref ID / Style Code section */}
      {onRefIdConfirm && (
        <div className="flex flex-col gap-2">
          <label
            htmlFor={refIdId}
            className="flex items-center gap-1.5 text-sm font-medium text-neutral-700 dark:text-neutral-300"
          >
            <MaterialSymbolsLinkRounded className="size-4" />
            {t('generation:style.use-ref-id')}
          </label>
          <p className="text-xs text-neutral-500 dark:text-neutral-400">
            {t('generation:style.use-ref-id-description')}
          </p>
          <div className="flex items-center gap-2">
            <UIInput
              id={refIdId}
              variant="rounded-normal"
              placeholder={t('generation:style.ref-id.placeholder')}
              value={refIdDraft}
              onChange={e => onRefIdDraftChange(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && refIdDraft) onRefIdConfirm()
              }}
              className="text-base sm:text-sm flex-1 font-mono"
            />
            <AriaButton
              onPress={onRefIdConfirm}
              isDisabled={!refIdDraft}
              className={confirmButtonClassName}
            >
              <MaterialSymbolsCheckRounded className="size-5" />
            </AriaButton>
          </div>
        </div>
      )}
    </div>
  )
}

/* ─── Desktop layout ─── */

function DesktopStyleContent(props: {
  allTags: { id: string; label: string }[]
  selectedTags: Set<string>
  onTagsChange: (tags: Set<string>) => void
  filteredStyles: StyleKeyItem[]
  modelType: string
  selectedId?: string
  onSelect: (id: string) => void
  selectedStyle?: StyleKeyItem
  onClear?: () => void
}) {
  const {
    allTags,
    selectedTags,
    onTagsChange,
    filteredStyles,
    modelType,
    selectedId,
    onSelect,
    selectedStyle,
    onClear,
  } = props
  const { t } = useTranslation(['generation'])

  return (
    <div className="flex gap-4 -mb-4 msm:-mb-6 h-[min(70dvh,600px)]">
      {/* Left panel: tags + style grid */}
      <div className="flex-1 flex flex-col gap-3 h-full">
        {onClear && (
          <AriaButton
            onPress={onClear}
            className={cx(
              'w-full rounded-lg px-4 py-2 text-sm font-medium text-center transition-colors',
              'ring-1 ring-inset ring-black/10 dark:ring-white/10',
              'hover:bg-black/5 dark:hover:bg-white/5',
            )}
          >
            {t('generation:style.selector.no-style')}
          </AriaButton>
        )}
        <TagFilters
          allTags={allTags}
          selectedTags={selectedTags}
          onTagsChange={onTagsChange}
        />
        <div className="overflow-y-auto h-full">
          {filteredStyles.length === 0 ? (
            <div className="flex items-center justify-center h-full text-sm text-zinc-400 dark:text-zinc-500">
              {t('generation:style.selector.no-results')}
            </div>
          ) : (
            <div className="grid grid-cols-[repeat(auto-fill,minmax(140px,1fr))] gap-3">
              {filteredStyles.map(item => (
                <StyleCard
                  key={item.id}
                  item={item}
                  modelType={modelType}
                  isSelected={item.id === selectedId}
                  onPress={() => onSelect(item.id)}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Right panel: showcase (CSS masonry) */}
      {selectedStyle && (
        <div className="w-96 flex flex-col gap-3 shrink-0 h-full border-l border-zinc-200 dark:border-zinc-700 pl-4 pr-2 pt-2">
          <div className="text-sm font-semibold uppercase text-zinc-500 dark:text-zinc-400">
            {t('generation:style.selector.showcase')}
          </div>
          <h3 className="text-base font-semibold">
            {t([
              `generation:model-style.${modelType}.${selectedStyle.id}`,
              selectedStyle.id,
            ])}
          </h3>
          <div className="overflow-y-auto">
            <div className="columns-2 gap-2 p-2">
              {selectedStyle.artworks
                .filter(a => a.mediaId)
                .map(artwork => (
                  <MediaThumbnail
                    key={artwork.mediaId}
                    artworkId={artwork.artworkId}
                    mediaId={artwork.mediaId}
                    masonry
                  />
                ))}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

/* ─── Mobile layout: Layer 1 — Style Library ─── */

function MobileStyleLibrary(props: {
  allTags: { id: string; label: string }[]
  selectedTags: Set<string>
  onTagsChange: (tags: Set<string>) => void
  filteredStyles: StyleKeyItem[]
  modelType: string
  onShowcase: (id: string) => void
  onUse: (id: string) => void
  onClear?: () => void
}) {
  const {
    allTags,
    selectedTags,
    onTagsChange,
    filteredStyles,
    modelType,
    onShowcase,
    onUse,
    onClear,
  } = props
  const { t } = useTranslation(['generation'])

  return (
    <div className="flex flex-col gap-3">
      {onClear && (
        <AriaButton
          onPress={onClear}
          className={cx(
            'w-full rounded-lg px-4 py-2 text-sm font-medium text-center transition-colors',
            'ring-1 ring-inset ring-black/10 dark:ring-white/10',
            'hover:bg-black/5 dark:hover:bg-white/5',
          )}
        >
          {t('generation:style.selector.no-style')}
        </AriaButton>
      )}
      {/* Horizontal scrolling tags */}
      <TagFilters
        allTags={allTags}
        selectedTags={selectedTags}
        onTagsChange={onTagsChange}
        horizontal
      />

      {/* Clear All button (only when tags are selected) */}
      {selectedTags.size > 0 && (
        <UIButton
          variant="ghost"
          size="small"
          className="self-start text-xs text-neutral-500 dark:text-neutral-400 underline"
          onPress={() => onTagsChange(new Set())}
        >
          {t('generation:style.selector.clear-all')}
        </UIButton>
      )}

      {/* Two-column card grid */}
      {filteredStyles.length === 0 ? (
        <div className="flex items-center justify-center py-12 text-sm text-zinc-400 dark:text-zinc-500">
          {t('generation:style.selector.no-results')}
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          {filteredStyles.map(item => (
            <MobileStyleCard
              key={item.id}
              item={item}
              modelType={modelType}
              onShowcase={() => onShowcase(item.id)}
              onUse={() => onUse(item.id)}
            />
          ))}
        </div>
      )}
    </div>
  )
}

/* ─── Shared tag filters ─── */

function TagFilters(props: {
  allTags: { id: string; label: string }[]
  selectedTags: Set<string>
  onTagsChange: (tags: Set<string>) => void
  horizontal?: boolean
}) {
  const { allTags, selectedTags, onTagsChange, horizontal } = props

  if (allTags.length === 0) return null

  return (
    <AriaTagGroup
      selectionMode="multiple"
      selectedKeys={selectedTags}
      onSelectionChange={sel => {
        if (sel === 'all') return
        onTagsChange(sel as Set<string>)
      }}
      className="text-sm"
    >
      <AriaTagList
        items={allTags}
        className={cx(
          'flex gap-2',
          horizontal
            ? 'overflow-x-auto flex-nowrap pb-1 scrollbar-none'
            : 'flex-wrap',
        )}
      >
        {item => (
          <AriaTag id={item.id} className="group" textValue={item.label}>
            <div
              className={cx(tagClassName, horizontal && 'whitespace-nowrap')}
            >
              {item.label}
            </div>
          </AriaTag>
        )}
      </AriaTagList>
    </AriaTagGroup>
  )
}

/* ─── Desktop style card ─── */

function StyleCard(props: {
  item: StyleKeyItem
  modelType: string
  isSelected: boolean
  onPress: () => void
}) {
  const { item, modelType, isSelected, onPress } = props
  const { t } = useTranslation(['generation'])
  const coverMediaId = item.artworks[0]?.mediaId

  const label = t([`generation:model-style.${modelType}.${item.id}`, item.id])

  return (
    <AriaButton
      onPress={onPress}
      className={styleCardStyles({ selected: isSelected })}
    >
      <div className="aspect-square overflow-hidden w-full">
        {coverMediaId && <MediaThumbnail mediaId={coverMediaId} />}
      </div>
      <div className="px-2 py-1.5 text-xs font-medium truncate w-full text-left">
        {label}
      </div>
      <StyleTagLine tags={item.tags} />
    </AriaButton>
  )
}

/* ─── Mobile style card ─── */

function MobileStyleCard(props: {
  item: StyleKeyItem
  modelType: string
  onShowcase: () => void
  onUse: () => void
}) {
  const { item, modelType, onShowcase, onUse } = props
  const { t } = useTranslation(['generation'])
  const coverMediaId = item.artworks[0]?.mediaId
  const label = t([`generation:model-style.${modelType}.${item.id}`, item.id])

  return (
    <div
      className={cx(
        'relative flex flex-col rounded-xl overflow-hidden text-left',
        'ring-1 ring-black/10 dark:ring-white/10 shadow-sm',
      )}
    >
      <AriaButton
        className="flex flex-col items-stretch cursor-pointer"
        onPress={onShowcase}
      >
        <div className="aspect-square overflow-hidden w-full">
          {coverMediaId && <MediaThumbnail mediaId={coverMediaId} />}
        </div>
        <div className="px-2 pt-1.5 pb-1 text-xs font-medium truncate w-full text-left">
          {label}
        </div>
        <StyleTagLine tags={item.tags} />
      </AriaButton>
      <div className="flex gap-1 px-2 pb-2">
        <AriaButton
          className={mobileActionButton({ variant: 'secondary' })}
          onPress={onShowcase}
        >
          {t('generation:style.selector.showcase')}
        </AriaButton>
        <AriaButton
          className={mobileActionButton({ variant: 'primary' })}
          onPress={onUse}
        >
          {t('generation:style.selector.use')}
        </AriaButton>
      </div>
    </div>
  )
}

/* ─── Style tag line (single-row, overflow hidden) ─── */

function StyleTagLine(props: { tags: string[] }) {
  const { t } = useTranslation(['generation'])

  if (props.tags.length === 0) return null

  return (
    <div className="flex gap-1 px-2 pb-1.5 overflow-hidden max-h-6">
      {props.tags.map(tag => (
        <span
          key={tag}
          className="text-[10px] px-1.5 py-0.5 rounded bg-black/5 dark:bg-white/10 text-zinc-500 dark:text-zinc-400 whitespace-nowrap shrink-0"
        >
          {t([`generation:style.tags.${tag}`, tag])}
        </span>
      ))}
    </div>
  )
}

/* ─── Media thumbnail ─── */

function MediaThumbnail(props: {
  artworkId?: string
  mediaId: string
  masonry?: boolean
}) {
  const url = getMediaUrl(props.mediaId, 'thumbnail')

  if (!url) {
    return (
      <div className="w-full aspect-square bg-zinc-100 dark:bg-zinc-800 animate-pulse rounded-md" />
    )
  }

  const body = (
    <img
      src={url}
      alt=""
      className={cx(
        'w-full object-cover transition-all',
        props.masonry &&
          'mb-2 break-inside-avoid rounded-md hover:shadow-lg hover:outline outline-2 outline-purple-600',
      )}
    />
  )

  if (props.masonry && props.artworkId) {
    return (
      <a
        href={`/artwork/${props.artworkId}`}
        target="_blank"
        rel="noopener noreferrer"
      >
        {body}
      </a>
    )
  }

  return body
}
